import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

const Edit = ({ open, onClose, editData, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/categories",
    token
  );
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedShop, setSelectedShop] = useState("");

  useEffect(() => {
    setName(editData?.name);
    setDescription(editData?.description);
    setSelectedShop(editData?.shop);
  }, [editData]);

  useEffect(() => {
    if (!shopLoading && shops && shops.length === 1) {
      setSelectedShop(shops[0].id);
    }
  }, [shopLoading, shops]);

  const handleSubmit = () => {
    const formData = {
      name: name.trim(),
      description,
      shop: selectedShop,
    };
    handleUpdate(editData?.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setName("");
      setDescription("");
      if (shops && shops.length === 1) {
        setSelectedShop(shops[0].id);
      } else {
        setSelectedShop("");
      }
      onClose();
    }
  }, [isSuccess, onClose, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("category.editCategory")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("category.categoryName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            margin="dense"
            label={t("category.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            select
            margin="dense"
            label={t("category.shop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops &&
              shops.map((shop) => (
                <MenuItem key={shop.id} value={shop.id}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("category.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !selectedShop}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("category.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Edit;
